'use client'

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import type { Customer } from '@/lib/db/schemas/customers'
import type { Serialized } from '@/lib/db/mappers'
import type { Referral } from '@/lib/db/schemas/referrals'
import type { IntakeSurvey } from '@/lib/db/schemas/leads'
import { fetchApi, ApiError } from '@/lib/utils/fetch-api'
import { parseQueryErrorMessage } from '@/lib/api/parse-query-error'
import { logError } from '@/lib/errors'
import type { SeminarResponseData } from '@/app/projects/customers/backend/services/customer-detail-service'
import { applyStatusToListCaches } from '@/lib/hooks/use-customers-status-cache'

type ApiCustomer = Serialized<Customer>

/** /api/customers/[id] が返す詳細レスポンス（extended fields含む） */
export type ApiCustomerDetail = ApiCustomer & {
  referrals?: Referral[]
  intakeSurvey?: IntakeSurvey | null
  receptionRecord?: {
    income?: string | null
    workplace?: string | null
    partner_workplace?: string | null
    partner_income?: string | null
    desired_area?: string | null
    self_funding?: string | null
    family_count?: string | null
    [key: string]: unknown
  } | null
}

/** /api/customers/[id]/reception-data が返すレスポンス */
export type ApiCustomerReceptionData = {
  receptionRecord: Record<string, unknown> | null
  seminarResponse: SeminarResponseData | null
  // 2026-07-04: reservation.staff (営業担当) と event_time_slot_staff.staff (講師)
  //   + 全来場履歴 (日付・時間・講師・営業担当・キャンセル)
  reservationInfo?: {
    reservationStaff: string | null
    eventInstructor: string | null
    visits?: Array<{
      eventDate: string | null
      timeSlot: string | null
      reservationStaff: string | null
      eventInstructor: string | null
      isCancelled: boolean
      customerStatus: string | null
    }>
  } | null
}

type PipelineStatus = NonNullable<ApiCustomer['pipelineStatus']>

// ============================================
// Filter Types
// ============================================
export interface CustomerFilterOptions {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  status?: PipelineStatus
  assignedTo?: string
  search?: string
  infinite?: boolean
  paginated?: boolean
  rank?: 'A' | 'B' | 'C' | 'D'
  landOwned?: boolean
  forecastMonths?: number[]
  excludeEarlyStages?: boolean
}

export interface PaginatedResult<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
  hasMore: boolean
}

// ============================================
// Query Keys
// ============================================
export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (filters: CustomerFilterOptions) => [...customerKeys.lists(), filters] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerKeys.details(), id] as const,
  reception: (id: string) => [...customerKeys.details(), id, 'reception'] as const,
  pipeline: (status: PipelineStatus) => [...customerKeys.all, 'pipeline', status] as const,
  counts: () => [...customerKeys.all, 'counts'] as const,
}

// ============================================
// Helper: Build query string from filters
// ============================================
function buildCustomerParams(filters: CustomerFilterOptions): string {
  const params = new URLSearchParams()
  if (filters.paginated === false) {
    params.set('paginated', 'false')
  }
  if (filters.page != null) params.set('page', String(filters.page))
  if (filters.limit != null) params.set('limit', String(filters.limit))
  if (filters.sortBy) params.set('sortBy', filters.sortBy)
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder)
  if (filters.status) params.set('status', filters.status)
  if (filters.assignedTo) params.set('assignedTo', filters.assignedTo)
  if (filters.search) params.set('search', filters.search)
  if (filters.rank) params.set('rank', filters.rank)
  if (filters.landOwned !== undefined) params.set('land_owned', String(filters.landOwned))
  if (filters.forecastMonths?.length)
    params.set('forecast_months', filters.forecastMonths.join(','))
  if (filters.excludeEarlyStages) params.set('excludeEarlyStages', 'true')
  return params.toString()
}

// ============================================
// Hooks
// ============================================

/**
 * ページネーション付き顧客一覧取得
 */
export function useCustomers(
  filters: CustomerFilterOptions = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: customerKeys.list(filters),
    // 使わない画面では取りに行かない（既定は今までどおり取る）
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const qs = buildCustomerParams(filters)
      const res = await fetch(`/api/customers?${qs}`, {
        headers: { 'Content-Type': 'application/json' },
      })
      if (!res.ok) {
        throw new Error(await parseQueryErrorMessage(res))
      }
      const json = await res.json()
      // paginated=false: API returns { data: [...] } without pagination wrapper
      if (filters.paginated === false) {
        const data = json.data ?? json
        // 200 でも配列でない応答は契約崩れ=障害。[] に潰すと画面が「0件」に化けて
        // 失敗を隠す(偽0・use-g-plans PR #8941 と同型)。error に倒して isError に載せる。
        if (!Array.isArray(data)) {
          throw new Error('顧客一覧の応答形式が不正です（配列ではありません）')
        }
        return {
          data,
          total: data.length,
          page: 1,
          limit: 0,
          totalPages: 1,
          hasMore: false,
        } as PaginatedResult<ApiCustomer>
      }
      // API returns { data: [...], pagination: {...} } for paginated mode
      return {
        data: json.data,
        total: json.pagination?.total ?? 0,
        page: json.pagination?.page ?? 1,
        limit: json.pagination?.limit ?? 50,
        totalPages: json.pagination?.totalPages ?? 1,
        hasMore: json.pagination?.hasMore ?? false,
      } as PaginatedResult<ApiCustomer>
    },
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * 無限スクロール用顧客一覧取得
 */
export function useCustomersInfinite(filters: Omit<CustomerFilterOptions, 'page'> = {}) {
  return useInfiniteQuery({
    queryKey: customerKeys.list({ ...filters, infinite: true } as CustomerFilterOptions),
    queryFn: async ({ pageParam = 1 }) => {
      const qs = buildCustomerParams({ ...filters, page: pageParam })
      const res = await fetch(`/api/customers?${qs}`, {
        headers: { 'Content-Type': 'application/json' },
      })
      if (!res.ok) {
        throw new Error(await parseQueryErrorMessage(res))
      }
      const json = await res.json()
      return {
        data: json.data,
        total: json.pagination?.total ?? 0,
        page: json.pagination?.page ?? pageParam,
        limit: json.pagination?.limit ?? 50,
        totalPages: json.pagination?.totalPages ?? 1,
        hasMore: json.pagination?.hasMore ?? false,
      } as PaginatedResult<ApiCustomer>
    },
    getNextPageParam: (lastPage: PaginatedResult<ApiCustomer>) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
  })
}

/**
 * パイプラインステータス別顧客取得
 */
export function useCustomersByStatus(status: PipelineStatus) {
  return useQuery({
    queryKey: customerKeys.pipeline(status),
    queryFn: () =>
      fetchApi<ApiCustomer[]>(
        `/api/customers?paginated=false&status=${encodeURIComponent(status)}`
      ),
  })
}

/**
 * 単一顧客取得
 */
export function useCustomer(id: string) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: () => fetchApi<ApiCustomerDetail>(`/api/customers/${id}`),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    // 一覧→詳細遷移時はキャッシュから即座にデータを返す（スケルトン非表示）
    placeholderData: () => {
      const cache = queryClient.getQueriesData<{ data: ApiCustomer[] }>({
        queryKey: customerKeys.lists(),
      })
      for (const [, result] of cache) {
        const found = result?.data?.find((c) => c.id === id)
        if (found) return found as ApiCustomerDetail
      }
      return undefined
    },
    // 4xx (認証/認可エラー) は即座に失敗させる。5xx・タイムアウトは1回だけリトライ。
    // デフォルト3回×25s=75秒のスケルトン固まり問題 (Issue: お客様管理２開けない) を防止。
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.isClientError) return false
      return failureCount < 1
    },
  })
}

/**
 * 顧客受付データ取得（kintone + seminar 遅延フェッチ）
 * メインの顧客データが届いた後に発火させることでDB接続の競合を防ぐ。
 */
export function useCustomerReceptionData(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: customerKeys.reception(id),
    queryFn: () => fetchApi<ApiCustomerReceptionData>(`/api/customers/${id}/reception-data`),
    enabled: !!id && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.isClientError) return false
      return failureCount < 1
    },
  })
}

/**
 * パイプラインカウント取得
 * サーバー側でGROUP BYカウントを実行（全件取得しない）
 */
export function usePipelineCounts() {
  return useQuery({
    queryKey: customerKeys.counts(),
    queryFn: async () => {
      const counts = await fetchApi<Record<string, number>>('/api/customers/counts')
      return counts as Record<PipelineStatus, number>
    },
  })
}

/**
 * 顧客作成
 */
export function useCreateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (customer: Partial<ApiCustomer>) =>
      fetchApi<ApiCustomer>('/api/customers', {
        method: 'POST',
        body: JSON.stringify(customer),
      }),
    onSuccess: (newCustomer) => {
      queryClient.setQueryData(customerKeys.detail(newCustomer.id), newCustomer)
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: customerKeys.counts() })
    },
  })
}

/**
 * 顧客更新
 */
export function useUpdateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      updates,
      expectedUpdatedAt,
    }: {
      id: string
      updates: Partial<ApiCustomer>
      // 楽観ロック token (Issue #3467 で必須化)。明示指定が無ければ React Query キャッシュ
      // (customerKeys.detail) の最新 updatedAt を自動添付する。
      expectedUpdatedAt?: string
    }) => {
      const cached = queryClient.getQueryData<ApiCustomer>(customerKeys.detail(id))
      let token = expectedUpdatedAt ?? cached?.updatedAt
      // 2026-06-20 業務オーナー報告: 「保存に失敗しました」が再現する事例の根本対応。
      // PWA キャッシュや React Query 未初期化等で cached?.updatedAt が undefined になると、
      // 従来は throw → 画面 catch → 詳細隠蔽 で原因不明の保存失敗を起こしていた。
      // 防御層として、token 未取得時は API GET で最新 updatedAt をその場で再取得する。
      if (!token) {
        try {
          const fresh = await fetchApi<ApiCustomer>(`/api/customers/${id}`)
          token = fresh.updatedAt
        } catch {
          // GET 失敗時は元の throw に倒れる (下のガード)
        }
      }
      if (!token) {
        throw new Error(
          '楽観ロック token (expectedUpdatedAt) が不足しています。GET でお客様情報を取得してから更新してください'
        )
      }
      // 🔴 409（他者が先に保存済み）を受けても自動再送しない。再送は他者の変更を黙って
      // 上書きしていた（lost update）。2026-08-12 owner 判断 A。経緯と根拠の検証:
      // docs/運用/同時編集-lost-update-owner判断-2026-08-12.md
      return fetchApi<ApiCustomer>(`/api/customers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ ...updates, expectedUpdatedAt: token }),
      })
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: customerKeys.detail(id) })

      const previousCustomer = queryClient.getQueryData<ApiCustomer>(customerKeys.detail(id))

      if (previousCustomer) {
        // 2026-06-20 業務オーナー報告: 楽観的 update で updatedAt をフロント時刻に
        // 書き換えていたため、後続の mutationFn が cached.updatedAt = フロント時刻 を
        // 拾ってサーバー側 actual updated_at と毎回ずれ → 必ず 409 conflict が起きていた
        // 構造的バグ。updatedAt はサーバー側の正しい値が onSuccess で来るため触らない。
        queryClient.setQueryData(customerKeys.detail(id), {
          ...previousCustomer,
          ...updates,
          // 契約・プラン依頼と同じく旧値を**明示復元**する。`...updates` に updatedAt が
          // 混ざると token cache が汚れる（Codex SO 2026-08-12・3ドメイン同一形の不変条件）。
          updatedAt: previousCustomer.updatedAt,
        })
      }

      return { previousCustomer }
    },
    // 🔴 保存成功のたびにサーバ応答を反映する。無いと連続保存の 2 回目が旧 token を送り
    // 「自分の直前の保存」に対して 409 になる（onSettled の再取得は待たれない）。
    // 🔴 置換ではなく merge。GET は referrals / intakeSurvey / receptionRecord を返すが
    // PATCH は customers 行のみで、置換すると詳細画面からそれらが消える。
    // 詳細: docs/運用/同時編集-lost-update-owner判断-2026-08-12.md
    onSuccess: (updated, { id }) => {
      if (!updated?.updatedAt) return
      queryClient.setQueryData(customerKeys.detail(id), (prev: ApiCustomer | undefined) =>
        prev ? { ...prev, ...updated } : updated
      )
    },
    onError: (err, variables, context) => {
      if (context?.previousCustomer) {
        queryClient.setQueryData(customerKeys.detail(variables.id), context.previousCustomer)
      }
    },
    onSettled: (_, __, { id }) => {
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: customerKeys.counts() })
    },
  })
}

/**
 * 顧客ステータス更新
 */
export function useUpdateCustomerStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      status,
      expectedUpdatedAt,
    }: {
      id: string
      status: PipelineStatus
      // 楽観ロック token (Issue #3467 で必須化)。明示指定が無ければ React Query キャッシュ
      // (customerKeys.detail) の最新 updatedAt を自動添付する。
      expectedUpdatedAt?: string
    }) => {
      const cached = queryClient.getQueryData<ApiCustomer>(customerKeys.detail(id))
      let token = expectedUpdatedAt ?? cached?.updatedAt
      // 🔴 useUpdateCustomer と同じ GET fallback。無いと **失注顧客の「復帰」が必ず失敗する**
      // （lost-customers ページは一覧データだけから useCustomerMutations().updateStatus() を呼び、
      //  そちらは expectedUpdatedAt を渡さないため detail cache が空＝token 不足で即 throw になる）。
      // Codex SO 2026-08-13。3ドメイン同一形の不変条件から漏れていた最後の1経路。
      if (!token) {
        try {
          const fresh = await fetchApi<ApiCustomer>(`/api/customers/${id}`)
          token = fresh.updatedAt
        } catch (err) {
          // 握り潰さない: 失敗理由を残したうえで下のガードへ倒す（token 無しの PATCH は投げない）。
          logError(err, `[useUpdateCustomerStatus] token 再取得の GET に失敗 (id: ${id})`)
        }
      }
      if (!token) {
        throw new Error(
          '楽観ロック token (expectedUpdatedAt) が不足しています。GET でお客様情報を取得してから更新してください'
        )
      }
      return fetchApi<ApiCustomer>(`/api/customers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ pipelineStatus: status, expectedUpdatedAt: token }),
      })
    },
    onMutate: async ({ id, status }) => {
      // detail + list の両方をキャンセル＆オプティミスティック更新
      await queryClient.cancelQueries({ queryKey: customerKeys.detail(id) })
      await queryClient.cancelQueries({ queryKey: customerKeys.lists() })

      const previousCustomer = queryClient.getQueryData<ApiCustomer>(customerKeys.detail(id))

      if (previousCustomer) {
        // 2026-06-20 useUpdateCustomer と同じ 409 構造的バグ対策。updatedAt を触らない。
        queryClient.setQueryData(customerKeys.detail(id), {
          ...previousCustomer,
          pipelineStatus: status,
          // updatedAt: 触らない (元の値維持で 409 conflict 防止)
        })
      }

      // リストキャッシュも即座に更新（カードが消えるのを防止）。
      // 実体は use-customers-status-cache.ts（onSuccess 追加で 80 行制限に触れたため責務分離）。
      const previousLists = applyStatusToListCaches(queryClient, customerKeys.lists(), id, status)

      return { previousCustomer, previousLists }
    },
    onError: (_err, variables, context) => {
      if (context?.previousCustomer) {
        queryClient.setQueryData(customerKeys.detail(variables.id), context.previousCustomer)
      }
      // リストキャッシュもロールバック
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key as readonly unknown[], data)
        }
      }
    },
    // 🔴 useUpdateCustomer と同一形。無いと detail cache の updatedAt が旧 token のまま残り、
    // ステータス変更の直後に通常保存すると「自分の直前の保存」に対して 409 になる。
    // 409 の自動再送を廃止した後は画面上の保存失敗として露出する（Codex SO 2026-08-12）。
    // merge にするのは PATCH が customers 行しか返さないため（GET 専用項目を消さない）。
    onSuccess: (updated, { id }) => {
      if (!updated?.updatedAt) return
      queryClient.setQueryData(customerKeys.detail(id), (prev: ApiCustomer | undefined) =>
        prev ? { ...prev, ...updated } : updated
      )
    },
    onSettled: (_, __, { id }) => {
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: customerKeys.counts() })
    },
  })
}

/**
 * 顧客削除
 */
export function useDeleteCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => fetchApi<void>(`/api/customers/${id}`, { method: 'DELETE' }),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: customerKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: customerKeys.counts() })
    },
  })
}
